import { Granularity, TimeRangeConfig, TimeRangeKind } from '@/api/generated/model';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import { getDateTRangeFromKind } from '@/utils/get-date-range-from-kind';
import { formatToLocaleDateValue, getTimezoneName } from '@/utils/date.util';
import dayjs from 'dayjs';

export const TREND_BUCKET_TARGET = 12;

/** Minimum month buckets (or non-zero monthly points) before keeping monthly trend granularity. */
export const MIN_MONTH_BUCKETS_FOR_TREND = 6;

/** Longest calendar month — caps daily trend queries (org chart + segment peer band). */
export const MAX_DAILY_TREND_BUCKETS = 31;

export type TrendQueryConfig = {
  time_range: TimeRangeConfig;
  granularity: Granularity;
  bucketKind: TimeRangeKind;
};

export type TrendQueryOptions = {
  /** When the monthly chart has fewer non-zero buckets than the threshold, size down to weekly. */
  nonZeroMonthBuckets?: number;
};

type GranularityUnit = 'day' | 'week' | 'month' | 'year';

function granularityUnit(granularity: Granularity): GranularityUnit {
  switch (granularity) {
    case Granularity.day:
      return 'day';
    case Granularity.week:
      return 'week';
    case Granularity.month:
      return 'month';
    case Granularity.year:
      return 'year';
    default:
      return 'day';
  }
}

function estimateBucketCount(start: dayjs.Dayjs, end: dayjs.Dayjs, granularity: Granularity): number {
  const unit = granularityUnit(granularity);
  return Math.max(1, end.startOf(unit).diff(start.startOf(unit), unit) + 1);
}

function startForBucketCount(end: dayjs.Dayjs, granularity: Granularity, bucketCount: number): dayjs.Dayjs {
  const unit = granularityUnit(granularity);
  const span = Math.max(1, bucketCount) - 1;
  return end.subtract(span, unit).startOf(unit);
}

function isDailyTrendRange(pageStart: dayjs.Dayjs, pageEnd: dayjs.Dayjs, timeRange: TimeRangeConfig): boolean {
  const kind = timeRange.kind ?? TimeRangeKind['30_days'];

  if (
    kind === TimeRangeKind.today ||
    kind === TimeRangeKind.yesterday ||
    kind === TimeRangeKind['7_days'] ||
    kind === TimeRangeKind.this_month
  ) {
    return true;
  }

  if (kind === TimeRangeKind.custom) {
    return pageEnd.diff(pageStart, 'day') < 7;
  }

  return false;
}

/**
 * Month-first trend granularity: start at monthly buckets and size down to weekly
 * when the window (or observed non-zero monthly points) cannot support enough buckets.
 */
export function preferredTrendGranularity(
  pageStart: dayjs.Dayjs,
  pageEnd: dayjs.Dayjs,
  timeRange: TimeRangeConfig,
  nonZeroMonthBuckets?: number,
): Granularity {
  if (isDailyTrendRange(pageStart, pageEnd, timeRange)) {
    return Granularity.day;
  }

  const monthBucketsInWindow = estimateBucketCount(pageStart, pageEnd, Granularity.month);
  const monthBuckets =
    nonZeroMonthBuckets === undefined
      ? monthBucketsInWindow
      : Math.min(monthBucketsInWindow, nonZeroMonthBuckets);

  if (monthBuckets >= MIN_MONTH_BUCKETS_FOR_TREND) {
    return Granularity.month;
  }

  return Granularity.week;
}

/** Count trend buckets where at least one series has a non-zero value. */
export function countNonZeroTrendBuckets(bucketTotals: number[]): number {
  return bucketTotals.reduce((count, total) => count + (total > 0 ? 1 : 0), 0);
}

/** Re-anchor chart trend windows to effective now without inheriting stable-now preset bounds. */
function resolveTrendPageRange(
  selectedRange: TimeRangeConfig | undefined,
  pageRange: TimeRangeConfig,
  kind: TimeRangeKind,
  timezone: string,
  now: Date,
  effectiveNow?: Date,
): TimeRangeConfig {
  if (!effectiveNow) {
    return pageRange;
  }

  if (kind === TimeRangeKind.custom) {
    return resolveNebulyPageTimeRange(selectedRange, now, effectiveNow) ?? pageRange;
  }

  return (
    resolveNebulyPageTimeRange(
      {
        kind,
        start: '',
        end: '',
        timezone,
        recompute: selectedRange?.recompute ?? true,
      },
      effectiveNow,
      effectiveNow,
    ) ?? pageRange
  );
}

function resolveTrendGranularity(
  pageStart: dayjs.Dayjs,
  pageEnd: dayjs.Dayjs,
  maxBuckets: number,
  timeRange: TimeRangeConfig,
  nonZeroMonthBuckets?: number,
): { granularity: Granularity; trendStart: dayjs.Dayjs } {
  const granularity = preferredTrendGranularity(pageStart, pageEnd, timeRange, nonZeroMonthBuckets);

  const unit = granularityUnit(granularity);
  const availableBuckets = estimateBucketCount(pageStart, pageEnd, granularity);
  const useFullRange = granularity === Granularity.day;
  const bucketCount = useFullRange ? availableBuckets : Math.min(availableBuckets, maxBuckets);
  const trendStart = useFullRange
    ? pageStart.startOf(unit)
    : startForBucketCount(pageEnd, granularity, bucketCount);

  return { granularity, trendStart };
}

export function getFixedRangeTrendQuery(
  kind: Extract<TimeRangeKind, '12_months' | '6_months'>,
  granularity: Extract<Granularity, 'month' | 'week'>,
  now: Date = new Date(),
  effectiveNow?: Date,
  timezone: string = getTimezoneName(),
): TrendQueryConfig {
  const pageRange = resolveNebulyPageTimeRange(
    {
      kind,
      start: '',
      end: '',
      timezone,
      recompute: true,
    },
    now,
    effectiveNow,
  );

  if (!pageRange) {
    throw new Error(`Unable to resolve ${kind} trend time range`);
  }

  const trendPageRange = resolveTrendPageRange(
    undefined,
    pageRange,
    kind,
    timezone,
    now,
    effectiveNow,
  );

  const pageEnd = dayjs(trendPageRange.end);
  const pageStart = dayjs(trendPageRange.start);
  const availableBuckets = estimateBucketCount(pageStart, pageEnd, granularity);
  const bucketCount = Math.min(availableBuckets, TREND_BUCKET_TARGET);
  const trendStart = startForBucketCount(pageEnd, granularity, bucketCount);

  return {
    time_range: {
      kind: TimeRangeKind.custom,
      start: formatToLocaleDateValue(trendStart.valueOf()),
      end: trendPageRange.end,
      timezone: trendPageRange.timezone ?? timezone,
      recompute: false,
    },
    granularity,
    bucketKind: kind,
  };
}

export function getTrendQuery(
  selectedRange: TimeRangeConfig | undefined,
  now: Date = new Date(),
  effectiveNow?: Date,
  options?: TrendQueryOptions,
): TrendQueryConfig {
  const timezone = selectedRange?.timezone ?? getTimezoneName();
  const kind = selectedRange?.kind ?? TimeRangeKind['30_days'];

  const pageRange =
    resolveNebulyPageTimeRange(selectedRange, now, effectiveNow) ??
    resolveNebulyPageTimeRange(
      getDateTRangeFromKind(
        {
          kind,
          start: '',
          end: '',
          timezone,
          recompute: true,
        },
        now,
      ),
      now,
      effectiveNow,
    );

  if (!pageRange) {
    throw new Error('Unable to resolve AI proficiency trend time range');
  }

  const trendPageRange = resolveTrendPageRange(
    selectedRange,
    pageRange,
    kind,
    timezone,
    now,
    effectiveNow,
  );

  const pageEnd = dayjs(trendPageRange.end);
  const pageStart = dayjs(trendPageRange.start);
  const { granularity, trendStart } = resolveTrendGranularity(
    pageStart,
    pageEnd,
    TREND_BUCKET_TARGET,
    trendPageRange,
    options?.nonZeroMonthBuckets,
  );

  return {
    time_range: {
      kind: TimeRangeKind.custom,
      start: formatToLocaleDateValue(trendStart.valueOf()),
      end: trendPageRange.end,
      timezone: trendPageRange.timezone ?? timezone,
      recompute: false,
    },
    granularity,
    bucketKind: kind,
  };
}

export function formatTrendBucketLabel(
  bucket: string,
  granularity: Granularity,
  bucketKind: TimeRangeKind,
  isLast: boolean,
): string {
  const date = dayjs(bucket);
  if (!date.isValid()) return bucket;

  if (granularity === Granularity.week) {
    return date.format('MMM D');
  }

  if (granularity === Granularity.day) {
    return date.format('MMM D');
  }

  if (granularity === Granularity.year) {
    return date.format('YYYY');
  }

  if (bucketKind === TimeRangeKind['3_months']) {
    return `Q${date.quarter()}`;
  }

  return isLast ? date.format('MMM') : date.format('MMM');
}

export function formatOverTimeTooltipMonth(bucketLabel: string): string {
  return bucketLabel.split(' ')[0] ?? bucketLabel;
}
export function formatSegmentTrendTooltipHeader(
  monthLabel: string,
  rank: number,
  totalSegments: number,
  segmentNounPlural: string,
): string {
  if (rank > 0 && totalSegments > 0) {
    return `${monthLabel} · #${rank} of ${totalSegments} ${segmentNounPlural}`;
  }
  return monthLabel;
}
